import React, { useState } from "react";
import { Card, Badge, Button, Spinner } from "react-bootstrap";
import {
  CalendarDays,
  Play,
  Square,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Undo2,
  CircleCheckBig,
  Video,
} from "lucide-react";
import TopicRow from "./TopicRow";
import { ConfirmModal } from "../ui";

/**
 * SyllabusDayCard
 *
 * Renders one syllabus day as a collapsible card in the timeline.
 *
 * Props:
 *   - session         : Object  (BatchSyllabusSession document)
 *   - isCurrent       : Boolean (is this the active/current day?)
 *   - isExpanded       : Boolean
 *   - onToggleExpand   : Function
 *   - onStart          : Function
 *   - onEnd            : Function
 *   - onRestart        : Function  (in_progress → scheduled)
 *   - onReopen         : Function  (completed → in_progress)
 *   - onMarkCompleted  : Function  (scheduled/in_progress → completed)
 *   - onToggleTopic    : Function(topicIndex)
 *   - onMapRecording   : Function(topicIndex, topicName)
 *   - onMapMaterial    : Function(topicIndex, topicName)
 *   - linkedMaterials  : Object { [topicIndex]: count }
 *   - actionLoading    : Boolean
 *   - batchTiming      : Object { startTime, endTime } (optional)
 */
const SyllabusDayCard = ({
  session,
  isCurrent,
  isExpanded,
  onToggleExpand,
  onStart,
  onEnd,
  onRestart,
  onReopen,
  onMarkCompleted,
  onToggleTopic,
  onMapRecording,
  onMapMaterial,
  linkedMaterials,
  actionLoading,
  batchTiming,
}) => {
  const [confirmAction, setConfirmAction] = useState(null);

  if (!session) return null;

  const statusConfig = {
    scheduled:   { label: "Scheduled",   icon: CalendarDays },
    in_progress: { label: "In Progress", icon: Play },
    completed:   { label: "Completed",   icon: CheckCircle2 },
    skipped:     { label: "Skipped",     icon: Clock },
  };

  const current = statusConfig[session.status] || statusConfig.scheduled;
  const StatusIcon = current.icon;

  const completedTopics = session.topics?.filter((t) => t.status === "completed").length || 0;
  const totalTopics = session.topics?.length || 0;

  // Format time helper
  const formatTime = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  return (
  <>
    <Card className={`syllabus-day-card ${isCurrent ? "sdc-current" : ""} ${isExpanded ? "sdc-expanded" : ""}`}>
      {/* ── Clickable header (always visible) ── */}
      <Card.Header className="sdc-header" onClick={onToggleExpand} role="button">
        <div className="sdc-header-left">
          <span className="sdc-chevron">
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </span>
          <div className="sdc-day-icon">
            <CalendarDays size={18} />
          </div>
          <div className="sdc-header-text">
            <span className="sdc-title">
              Day {session.dayNumber} — {session.title}
            </span>
            {/* Batch timing display */}
            {batchTiming?.startTime && batchTiming?.endTime && (
              <span className="sdc-timing">
                ⏰ {formatTime(batchTiming.startTime)} – {formatTime(batchTiming.endTime)}
              </span>
            )}
            {/* Collapsed summary */}
            {!isExpanded && totalTopics > 0 && (
              <span className="sdc-summary">
                {completedTopics}/{totalTopics} topics completed
              </span>
            )}
          </div>
        </div>
        <div className="sdc-header-right">
          {isCurrent && session.status !== "completed" && (
            <span className="sdc-current-badge">Current</span>
          )}
          <Badge className={`sdc-status-badge sdc-status-${session.status}`}>
            <StatusIcon size={13} />
            {current.label}
          </Badge>
        </div>
      </Card.Header>

      {/* ── Expanded body ── */}
      {isExpanded && (
        <>
          {/* Topics */}
          <Card.Body className="sdc-body">
            <div className="sdc-topics-header">
              <span className="sdc-topics-title">Topics</span>
              <span className="sdc-topics-count">
                {completedTopics}/{totalTopics} completed
              </span>
            </div>

            <div className="sdc-topics-list">
              {session.topics?.map((topic, index) => (
                <TopicRow
                  key={index}
                  index={index}
                  topic={topic}
                  sessionStatus={session.status}
                  onToggle={() => onToggleTopic(index)}
                  onMapRecording={() => onMapRecording(index, topic.name)}
                  onMapMaterial={() => onMapMaterial?.(index, topic.name)}
                  linkedMaterialCount={linkedMaterials?.[index] || 0}
                />
              ))}
              {(!session.topics || session.topics.length === 0) && (
                <div className="sdc-no-topics">No topics for this day.</div>
              )}
            </div>
          </Card.Body>

          {/* ── Footer actions ── */}
          <Card.Footer className="sdc-footer">
            {/* SCHEDULED → Start button */}
            {session.status === "scheduled" && (
              <div className="sdc-action-group">
                <Button
                  variant="primary"
                  className="sdc-action-btn"
                  onClick={onStart}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <Video size={16} className="me-2" />
                  )}
                  Start Session & Class
                </Button>
                <Button
                  variant="success"
                  className="sdc-action-btn"
                  onClick={() => {
                    setConfirmAction({
                      title: 'Mark Completed',
                      message: 'Mark this session as completed? All topics will be marked as completed.',
                      variant: 'success',
                      confirmText: 'Mark Completed',
                      onConfirm: () => { setConfirmAction(null); onMarkCompleted(); },
                    });
                  }}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <CircleCheckBig size={16} className="me-2" />
                  )}
                  Mark Completed
                </Button>
              </div>
            )}

            {/* IN_PROGRESS → Restart + End buttons */}
            {session.status === "in_progress" && (
              <div className="sdc-action-group">
                <Button
                  variant="outline-secondary"
                  className="sdc-action-btn"
                  onClick={() => {
                    setConfirmAction({
                      title: 'Restart Session',
                      message: 'Restart this session? Topic statuses will reset to pending. Recordings will be preserved.',
                      variant: 'warning',
                      confirmText: 'Restart',
                      onConfirm: () => { setConfirmAction(null); onRestart(); },
                    });
                  }}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <RotateCcw size={15} className="me-2" />
                  )}
                  Restart
                </Button>
                <Button
                  variant="danger"
                  className="sdc-action-btn"
                  onClick={onEnd}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <Square size={16} className="me-2" />
                  )}
                  End Session
                </Button>
                <Button
                  variant="success"
                  className="sdc-action-btn"
                  onClick={() => {
                    setConfirmAction({
                      title: 'Mark Completed',
                      message: 'Mark this session as completed? All topics will be marked as completed.',
                      variant: 'success',
                      confirmText: 'Mark Completed',
                      onConfirm: () => { setConfirmAction(null); onMarkCompleted(); },
                    });
                  }}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <CircleCheckBig size={16} className="me-2" />
                  )}
                  Mark Completed
                </Button>
              </div>
            )}

            {/* COMPLETED → Reopen button */}
            {session.status === "completed" && (
              <div className="sdc-action-group">
                <span className="sdc-completed-label">
                  <CheckCircle2 size={16} className="me-1" />
                  Session Completed
                </span>
                <Button
                  variant="outline-warning"
                  className="sdc-action-btn"
                  onClick={() => {
                    setConfirmAction({
                      title: 'Reopen Session',
                      message: 'Reopen this session? It will go back to in-progress so you can make changes.',
                      variant: 'warning',
                      confirmText: 'Reopen',
                      onConfirm: () => { setConfirmAction(null); onReopen(); },
                    });
                  }}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <Undo2 size={15} className="me-2" />
                  )}
                  Reopen
                </Button>
              </div>
            )}
          </Card.Footer>
        </>
      )}
    </Card>

    <ConfirmModal
      show={!!confirmAction}
      onHide={() => setConfirmAction(null)}
      onConfirm={() => confirmAction?.onConfirm()}
      title={confirmAction?.title || ''}
      message={confirmAction?.message || ''}
      confirmText={confirmAction?.confirmText || 'Confirm'}
      variant={confirmAction?.variant || 'danger'}
    />
  </>
  );
};

export default SyllabusDayCard;
